<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import LuciControlActions from './LuciControlActions.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
    query: route.query.assignee
      ? { assignee: route.query.assignee }
      : undefined,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});
const isDcaiWhatsappQrInbox = computed(
  () =>
    inbox.value?.provider === 'dcai_whatsapp_qr' &&
    Boolean(inbox.value?.additional_attributes?.qr_connection_id)
);
const canManageQrConnection = computed(
  () =>
    currentUser.value?.accounts?.find(account => account.id === accountId.value)
      ?.role === 'administrator'
);
const qrConnectionStatus = ref('reconnecting');
let qrStatusPoller;

const qrStatus = computed(() => {
  if (qrConnectionStatus.value === 'open') return 'connected';
  if (['creating', 'connecting'].includes(qrConnectionStatus.value)) {
    return 'reconnecting';
  }
  if (qrConnectionStatus.value === 'error') return 'error';
  return 'disconnected';
});

const refreshQrStatus = async () => {
  if (!isDcaiWhatsappQrInbox.value) return;
  try {
    const { data } = await InboxesAPI.getWhatsappQrConnectionStatus(
      inbox.value.additional_attributes.qr_connection_id
    );
    qrConnectionStatus.value = data.status;
  } catch {
    qrConnectionStatus.value = 'error';
  }
};

const stopQrStatusPolling = () => {
  clearInterval(qrStatusPoller);
  qrStatusPoller = null;
};

const startQrStatusPolling = () => {
  stopQrStatusPolling();
  if (!isDcaiWhatsappQrInbox.value) return;
  refreshQrStatus();
  qrStatusPoller = setInterval(refreshQrStatus, 10_000);
};

const manageQrConnection = () => {
  router.push({
    name: 'settings_inboxes_page_channel',
    params: { accountId: accountId.value, sub_page: 'whatsapp' },
    query: {
      provider: 'whatsapp_qr',
      connection: inbox.value.additional_attributes.qr_connection_id,
    },
  });
};

onMounted(() => {
  startQrStatusPolling();
});

watch(
  () => inbox.value?.id,
  () => startQrStatusPolling()
);

onBeforeUnmount(stopQrStatusPolling);

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContact.name }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <button
            v-if="isDcaiWhatsappQrInbox && canManageQrConnection"
            type="button"
            class="text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0"
            @click="manageQrConnection"
          >
            {{ $t(`INBOX_MGMT.ADD.WHATSAPP.QR.STATUS.${qrStatus}`) }}
          </button>
          <span v-else-if="isDcaiWhatsappQrInbox">
            {{ $t(`INBOX_MGMT.ADD.WHATSAPP.QR.STATUS.${qrStatus}`) }}
          </span>
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <LuciControlActions :conversation-id="currentChat.id" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
